import type { ReactNode } from "react";
import { formatInteger } from "../../lib/i18n/format";
import { heatLevel } from "./scale";
import { TipLayer, useTip } from "./tooltip";

export interface HeatColumn {
	key: string;
	/** The short header (a sport code, a day number). */
	label: string;
	/** The full name (a header's tooltip and the cell's). */
	title: string;
}

export interface HeatRow {
	key: string;
	/** The row header — text, or a button that opens the row. */
	header: ReactNode;
	/** The row's name, plain, for the cells' tooltips. */
	title: string;
	/** One count per column key; a missing key is 0. */
	values: ReadonlyMap<string, number>;
	/** The tooltip of one cell (its breakdown); defaults to the count. */
	tip?: (columnKey: string) => ReactNode;
	total: number;
}

/** The sequential steps (styles.css `--color-heat-*`): one hue, light → dark. */
const STEP = ["var(--color-heat-0)", "var(--color-heat-1)", "var(--color-heat-2)", "var(--color-heat-3)", "var(--color-heat-4)", "var(--color-heat-5)"];

/**
 * A heatmap that IS a table: rows × columns, each cell's count printed on its
 * step of the sequential navy scale (white text from step 4, so it always
 * clears contrast), the header row and the first column sticky while it
 * scrolls. Being a real `<table>`, it needs no separate table view.
 */
export function Heatmap({ columns, rows, corner, totalLabel }: { columns: readonly HeatColumn[]; rows: readonly HeatRow[]; corner: ReactNode; totalLabel: string }) {
	const tip = useTip();
	let max = 0;
	for (const r of rows) for (const v of r.values.values()) max = Math.max(max, v);

	return (
		<div className="max-h-[36rem] overflow-auto rounded-btn border border-rule">
			<table className="border-separate border-spacing-0 text-[0.8125rem]">
				<thead>
					<tr>
						<th scope="col" className="sticky top-0 left-0 z-20 border-rule border-b bg-paper px-[0.75rem] py-[0.375rem] text-left text-[0.75rem] text-muted">
							{corner}
						</th>
						{columns.map((c) => (
							<th
								key={c.key}
								scope="col"
								title={c.title}
								className="sticky top-0 z-10 min-w-[2.75rem] border-rule border-b bg-paper px-[0.25rem] py-[0.375rem] text-center font-mono text-[0.6875rem] text-muted"
							>
								<abbr title={c.title} className="no-underline">
									{c.label}
								</abbr>
							</th>
						))}
						<th scope="col" className="sticky top-0 z-10 border-rule border-b border-l bg-paper px-[0.5rem] py-[0.375rem] text-right text-[0.75rem] text-muted">
							{totalLabel}
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((r) => (
						<tr key={r.key}>
							<th scope="row" className="sticky left-0 z-10 whitespace-nowrap border-rule border-b bg-sheet px-[0.75rem] py-[0.25rem] text-left font-normal">
								{r.header}
							</th>
							{columns.map((c) => {
								const v = r.values.get(c.key) ?? 0;
								const level = heatLevel(v, max);
								return (
									<td
										key={c.key}
										className="border-sheet border-b-2 border-l-2 p-0 text-center"
										onPointerMove={(e) =>
											tip.at(
												e,
												<div className="flex flex-col gap-[0.125rem]">
													<p className="font-bold">
														{r.title} · {c.title}
													</p>
													{r.tip ? r.tip(c.key) : <span className="font-mono">{formatInteger(v)}</span>}
												</div>,
											)
										}
										onPointerLeave={tip.hide}
									>
										<span
											className={`flex h-[2rem] min-w-[2.75rem] items-center justify-center font-mono tabular-nums ${level >= 4 ? "font-bold text-white" : v > 0 ? "font-bold text-ink" : "text-muted/60"}`}
											style={{ background: STEP[level] }}
										>
											{formatInteger(v)}
										</span>
									</td>
								);
							})}
							<td className="border-rule border-b border-l bg-sheet px-[0.5rem] text-right font-bold font-mono tabular-nums">{formatInteger(r.total)}</td>
						</tr>
					))}
				</tbody>
			</table>
			<TipLayer tip={tip} />
		</div>
	);
}

/** The heat scale's key: five steps from few to many. */
export function HeatKey({ few, many }: { few: string; many: string }) {
	return (
		<span className="inline-flex items-center gap-[0.375rem] text-[0.75rem] text-muted">
			{few}
			<span aria-hidden="true" className="flex gap-[2px]">
				{STEP.slice(1).map((c) => (
					<span key={c} className="h-[0.625rem] w-[1.25rem] rounded-[0.125rem]" style={{ background: c }} />
				))}
			</span>
			{many}
		</span>
	);
}
